import { useEffect, useRef, useState } from "react";

const testimonials = [
  {
    quote:
      "Blue Digital AI completely transformed our inbound pipeline. We went from chasing cold leads to having qualified prospects book calls automatically — our close rate jumped 3x in 60 days.",
    name: "Marcus Reid",
    title: "Founder & CEO",
    company: "Apex Financial Group",
    avatar: "https://public.readdy.ai/ai/img_res/646c5e0575dfc4b8f4697d987c862fdb.jpg",
    metric: "3× Close Rate",
  },
  {
    quote:
      "Within the first week of deploying their AI follow-up system, we recovered 18 deals that had gone dark. It's like having a tireless sales rep working around the clock without the overhead.",
    name: "Priya Anand",
    title: "Head of Growth",
    company: "Meridian SaaS",
    avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20young%20south%20asian%20female%20professional%20smiling%20confidently%2C%20clean%20white%20background%2C%20sharp%20focus%2C%20soft%20studio%20lighting%2C%20high%20quality%20portrait%20photography%2C%20photorealistic&width=80&height=80&seq=t2&orientation=squarish",
    metric: "18 Deals Recovered",
  },
  {
    quote:
      "The onboarding was seamless and the ROI was visible within 30 days. Our team no longer spends hours manually following up — the system handles it all, and our revenue numbers reflect that.",
    name: "James Thornton",
    title: "VP of Sales",
    company: "Thornton Realty Partners",
    avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20confident%20tall%20caucasian%20male%20real%20estate%20executive%20in%20his%2040s%2C%20clean%20white%20background%2C%20sharp%20focus%2C%20warm%20studio%20lighting%2C%20high%20quality%20portrait%20photography%2C%20photorealistic&width=80&height=80&seq=t3&orientation=squarish",
    metric: "ROI in 30 Days",
  },
  {
    quote:
      "I was skeptical about AI automation — but the team at Blue Digital made it dead simple. Our lead response time dropped from 4 hours to under 2 minutes. Game-changing.",
    name: "Sofia Mendes",
    title: "Director of Marketing",
    company: "BrightPath Clinics",
    avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20latina%20female%20marketing%20director%20in%20her%2030s%2C%20confident%20smile%2C%20clean%20white%20background%2C%20soft%20studio%20lighting%2C%20high%20quality%20portrait%20photography%2C%20photorealistic&width=80&height=80&seq=t4&orientation=squarish",
    metric: "2-Min Response Time",
  },
  {
    quote:
      "We scaled from 200 to 850 qualified leads per month without adding a single SDR. The AI handles qualification, segmentation, and nurturing — it's genuinely impressive.",
    name: "Daniel Park",
    title: "Co-Founder",
    company: "Vortex Logistics",
    avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20young%20east%20asian%20male%20startup%20founder%20in%20his%2030s%2C%20clean%20white%20background%2C%20sharp%20focus%2C%20soft%20studio%20lighting%2C%20high%20quality%20portrait%20photography%2C%20photorealistic&width=80&height=80&seq=t5&orientation=squarish",
    metric: "4× Lead Volume",
  },
  {
    quote:
      "We tried two other agencies before Blue Digital AI. None of them delivered what this team did in the first month. The automation is smart, the support is exceptional, and results are real.",
    name: "Natalie Brooks",
    title: "CEO",
    company: "Summit Legal Advisory",
    avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20confident%20african%20american%20female%20CEO%20in%20her%2040s%20wearing%20a%20blazer%2C%20clean%20white%20background%2C%20sharp%20focus%2C%20warm%20studio%20lighting%2C%20high%20quality%20portrait%20photography%2C%20photorealistic&width=80&height=80&seq=t6&orientation=squarish",
    metric: "Best-in-Class Results",
  },
];

const TestimonialsSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const prev = () => setActiveIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
  const next = () => setActiveIndex((i) => (i + 1) % testimonials.length);

  const visibleCards = [
    testimonials[(activeIndex) % testimonials.length],
    testimonials[(activeIndex + 1) % testimonials.length],
    testimonials[(activeIndex + 2) % testimonials.length],
  ];

  return (
    <section ref={sectionRef} className="bg-[#f5f5f7] dark:bg-[#0d0d0d] py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0071e3] mb-4">
            Client Results
          </p>
          <h2 className="text-4xl lg:text-5xl font-semibold text-[#1d1d1f] dark:text-white leading-tight tracking-tight">
            What our clients say
          </h2>
          <p className="mt-4 text-base text-[#6e6e73] dark:text-white/50 max-w-xl mx-auto leading-relaxed">
            Real outcomes from real businesses. Every testimonial reflects a measurable, documented result.
          </p>
        </div>

        {/* Cards */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 delay-200 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {visibleCards.map((t, i) => (
            <div
              key={`${activeIndex}-${i}`}
              className={`bg-white dark:bg-white/[0.05] rounded-2xl p-8 flex flex-col gap-6 transition-all duration-500 ${
                i === 1 ? "md:scale-105 ring-1 ring-[#0071e3]/20" : ""
              }`}
            >
              {/* Stars */}
              <div className="flex gap-1">
                {[...Array(5)].map((_, s) => (
                  <i key={s} className="ri-star-fill text-[#0071e3] text-sm"></i>
                ))}
              </div>

              {/* Quote */}
              <p className="text-[#1d1d1f] dark:text-white/80 text-sm leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </p>

              {/* Metric badge */}
              <div className="inline-flex items-center gap-2 bg-[#f5f5f7] dark:bg-white/[0.08] rounded-full px-3 py-1 w-fit">
                <i className="ri-arrow-up-line text-[#0071e3] text-xs"></i>
                <span className="text-xs font-semibold text-[#1d1d1f] dark:text-white">{t.metric}</span>
              </div>

              {/* Author */}
              <div className="flex items-center gap-3 pt-2 border-t border-[#f5f5f7] dark:border-white/[0.08]">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-10 h-10 rounded-full object-cover object-top"
                />
                <div>
                  <p className="text-sm font-semibold text-[#1d1d1f] dark:text-white">{t.name}</p>
                  <p className="text-xs text-[#6e6e73] dark:text-white/50">{t.title}, {t.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-4 mt-12">
          <button
            onClick={prev}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-[#d2d2d7] dark:border-white/20 text-[#1d1d1f] dark:text-white hover:border-[#0071e3] hover:text-[#0071e3] transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>

          {/* Dots */}
          <div className="flex gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`rounded-full transition-all cursor-pointer ${
                  i === activeIndex
                    ? "w-6 h-2 bg-[#0071e3]"
                    : "w-2 h-2 bg-[#d2d2d7] dark:bg-white/20"
                }`}
              />
            ))}
          </div>

          <button
            onClick={next}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-[#d2d2d7] dark:border-white/20 text-[#1d1d1f] dark:text-white hover:border-[#0071e3] hover:text-[#0071e3] transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>

      </div>
    </section>
  );
};

export default TestimonialsSection;
